import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
} from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { createAgreementForm } from "@bca-agreement/agreement.constants";
import { AgreementType } from "@bca-agreement/models/_enums";
import { IIssuer } from "@bca-agreement/models/issuer";
import * as agreementActions from "@bca-agreement/store/agreement.actions";
import * as confirmationActions from "@bca-confirmation/store/confirmation.actions";
import { PageScrollService } from "@bca-core/modules/page-scroll/services/page-scroll.service";
import { AvoTrackingService } from "@bca-core/services/avo-tracking.service";
import * as receiptActions from "@bca-receipt/store/receipt.actions";
import {
  AddonIdentifier,
  MessageType,
  ScrollPositionId,
} from "@bca-shared/models/_enums";
import { IAgreement } from "@bca-shared/models/agreement";
import { APP_CONFIG, IAppConfiguration } from "@bca-shared/models/config";
import { Form } from "@bca-shared/models/form";
import { IProduct } from "@bca-shared/models/product";
import { AvoDataTransformService } from "@bca-shared/utils/avo/avo-data-transform.service";
import { AvoEventTypes } from "@bca-shared/utils/avo/avo-event-details.models";
import { AvoService } from "@bca-shared/utils/avo/avo.service";
import * as appActions from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import { selectAllProducts } from "@bca-store/store.selectors";
import { environment } from "@environments/environment";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { saveAs } from "file-saver";
import { Observable, Subject } from "rxjs";
import {
  filter,
  map,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from "rxjs/operators";

@Component({
  selector: "bca-agreement",
  templateUrl: "agreement.component.html",
  styleUrls: ["agreement.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgreementComponent extends Form<IAgreement> implements OnDestroy {
  agreementPreview$: Observable<{ blob: Blob; fileName: string }>;
  hasSelectedBikeAsService$: Observable<boolean>;
  startPaymentLoading$: Observable<boolean>;
  issuers$: Observable<IIssuer[]>;
  selectedAgreement$: Observable<IAgreement>;
  selectedIssuerId$: Observable<string>;
  hasErrorNotificationOpen$: Observable<boolean>;
  products: IProduct[];

  private readonly _destroy$ = new Subject<void>();

  private lastAuthorizedAgreementError: string | undefined;
  private lastDirectDebitAgreementError: string | undefined;
  private lastVerifyInfoAgreementError: string | undefined;

  AgreementType = AgreementType;

  constructor(
    private _store$: Store<IRootState>,
    private _formBuilder: FormBuilder,
    private _page: PageScrollService,
    private _translate: TranslateService,
    private _avoService: AvoService,
    private _avoTrackingService: AvoTrackingService,
    private _avoTranslateService: AvoDataTransformService,
    @Inject(APP_CONFIG) private _config: IAppConfiguration,
  ) {
    super(createAgreementForm(_formBuilder));

    this.selectedAgreement$ = this._store$.pipe(
      select(({ agreement }) => agreement.selectedAgreement),
    );

    this.hasErrorNotificationOpen$ = this._store$.pipe(
      select(({ app }) => app.lastMessage),
      map(
        (lastMessage) =>
          lastMessage != null && lastMessage.type === MessageType.ERROR,
      ),
    );

    this._store$
      .pipe(select(selectAllProducts), takeUntil(this._destroy$))
      .subscribe((products: IProduct[]) => {
        this.products = products;
      }),
      this.selectedAgreement$
        .pipe(
          takeUntil(this._destroy$),
          tap((agreement) =>
            agreement ? this.patchValue(agreement) : this.resetValue(),
          ),
        )
        .subscribe();

    this.selectedIssuerId$ = this.selectedAgreement$.pipe(
      map((agreement) => {
        if (agreement && agreement.issuer) {
          const { issuer } = agreement;
          return issuer.id;
        }
        return "0";
      }),
    );

    this.issuers$ = this._store$.pipe(
      select(({ agreement }) => agreement.issuers),
    );

    this.agreementPreview$ = this._store$.pipe(
      select(({ agreement }) => agreement.agreementPreviewAsBase64),
      filter((agreementPreviewAsBase64) => agreementPreviewAsBase64 != null),
      withLatestFrom(
        this._translate.get(
          "AGREEMENT.INPUT.AUTHORIZE_AGREEMENT.AGREEMENT.FILE.TITLE",
        ),
      ),
      map(([agreementPreviewAsBase64, fileName]) => {
        const type = environment.agreement.agreementPreviewFileType;
        const byteCharacters = window.atob(agreementPreviewAsBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        return {
          blob: new Blob([new Uint8Array(byteNumbers)], { type }),
          fileName,
        };
      }),
    );

    this.hasSelectedBikeAsService$ = this._store$.pipe(
      select(({ receipt }) => receipt.products),
      map((products) => {
        return products.map((product) =>
          product.services
            .filter((service) => service.selected)
            .some((service) => service.identifier === AddonIdentifier.Bike),
        );
      }),
      map((bikeServicesSelected) =>
        bikeServicesSelected.some((selected) => selected),
      ),
    );
    this.startPaymentLoading$ = this._store$.pipe(
      select(({ confirmation }) => confirmation.startPaymentLoading),
    );

    this._store$.dispatch(
      this._createAction(new agreementActions.FetchIssuers()),
    );

    this._store$.dispatch(
      this._createAction(new agreementActions.FetchAgreementPreview()),
    );

    this.handleAvoEvents("add_payment_info");
  }

  onCheckboxClick(event: Event, checkboxName: string): void {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;

    this.handleAvoEvents("checkbox_click", null, null, null, checkboxName, isChecked);

    // Additional logic if needed (e.g., analytics tracking)
  }

  getAuthorizedAgreementErrorKey(): string | undefined {
    const formTagId = "authorizedAgreementAndProductConditions";
    let errorMessage;
    if (this.isTouched(formTagId) && this.hasError(formTagId, "required")) {
      errorMessage = this._translate.instant(
        "AGREEMENT.INPUT.AUTHORIZE_AGREEMENT.ERROR_REQUIRED",
      );
    } else {
      errorMessage = undefined;
    }

    if (errorMessage && errorMessage !== this.lastAuthorizedAgreementError) {
      this.handleAvoEvents("form_error", null, formTagId, errorMessage);
      this.lastAuthorizedAgreementError = errorMessage;
    }

    if (!errorMessage) {
      this.lastAuthorizedAgreementError = undefined;
    }

    return errorMessage;
  }
  getDirectDebitErrorKey(): string | undefined {
    const formTagId = "authorizedDirectDebit";
    let errorMessage;
    if (this.isTouched(formTagId) && this.hasError(formTagId, "required")) {
      errorMessage = this._translate.instant(
        "AGREEMENT.INPUT.AUTHORIZE_DIRECT_DEBIT.ERROR_REQUIRED",
      );
    } else {
      errorMessage = undefined;
    }

    if (errorMessage && errorMessage !== this.lastDirectDebitAgreementError) {
      this.handleAvoEvents("form_error", null, formTagId, errorMessage);
      this.lastDirectDebitAgreementError = errorMessage;
    }

    if (!errorMessage) {
      this.lastDirectDebitAgreementError = undefined;
    }

    return errorMessage;
  }

  getVerifyInfoErrorKey(): string | undefined {
    const formTagId = "authorizedVerifyInfo";
    let errorMessage;
    if (this.isTouched(formTagId) && this.hasError(formTagId, "required")) {
      errorMessage = this._translate.instant(
        "AGREEMENT.INPUT.AUTHORIZE_VERIFY_INFO.ERROR_REQUIRED",
      );
    } else {
      errorMessage = undefined;
    }

    if (errorMessage && errorMessage !== this.lastVerifyInfoAgreementError) {
      this.handleAvoEvents("form_error", null, formTagId, errorMessage);
      this.lastVerifyInfoAgreementError = errorMessage;
    }

    if (!errorMessage) {
      this.lastVerifyInfoAgreementError = undefined;
    }

    return errorMessage;
  }

  getIssuerErrorKey(): boolean {
    const formTagId = "issuer";
    let errorMessage;
    if (this.isTouched("issuer.id") && this.hasError("issuer.id", "required")) {
      errorMessage = this._translate.instant(
        "AGREEMENT.INPUT.ISSUER.ERROR_REQUIRED",
      );
      this.handleAvoEvents("form_error", null, formTagId, errorMessage);
    } else {
      errorMessage = undefined;
    }

    if (errorMessage && errorMessage !== this.lastVerifyInfoAgreementError) {
      this.lastVerifyInfoAgreementError = errorMessage;
      return true;
    }

    if (!errorMessage) {
      this.lastVerifyInfoAgreementError = undefined;
      return false;
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  previousRoute(): void {
    this.handleAvoEvents("link_click");
    const { formGroup } = this;
    this._store$.dispatch(
      this._createAction(
        new agreementActions.SelectAgreement(formGroup.value as IAgreement),
      ),
    );
    this._store$.dispatch(this._createAction(new appActions.PreviousRoute()));
  }

  formSubmit(): void {
    const { formGroup } = this;
    this.handleAvoEvents("form_submit", formGroup.valid);
    this.hasErrorNotificationOpen$
      .pipe(
        take(1),
        filter((hasError) => !hasError),
      )
      .subscribe(() => {
        const agreement = formGroup.value as IAgreement;
        this.handleAvoEvents("button_click", formGroup.valid);

        this._store$.dispatch(
          this._createAction(new agreementActions.SelectAgreement(agreement)),
        );

        if (!formGroup.valid) {
          this.touchControls();
          this._store$.dispatch(
            this._createAction(new receiptActions.DeleteAgreement()),
          );
          this._page.scrollTo(ScrollPositionId.INPUT_INVALID, {
            timeout: 275,
            scrollOffset: 275,
          });
        } else {
          this._store$.dispatch(
            this._createAction(new receiptActions.AddAgreement(agreement)),
          );
          this._store$.dispatch(
            this._createAction(new confirmationActions.StartPayment()),
          );
        }
      });
  }

  onIssuerSelected(issuerId: string): void {
    const { formGroup, issuers$ } = this;
    const agreement = formGroup.value as IAgreement;

    issuers$
      .pipe(
        take(1),
        map((issuers) => issuers.find((issuer) => issuer.id === issuerId)),
      )
      .subscribe((issuer) => {
        this._store$.dispatch(
          this._createAction(
            new agreementActions.SelectAgreement({
              ...agreement,
              issuer,
            }),
          ),
        );
      });
  }

  showAgreement(type: AgreementType): void {
    const { condition_urls } = this._config;
    const lang = this._translate.currentLang;
    if (type === AgreementType.PREVIEW) {
      this.agreementPreview$
        .pipe(
          take(1),
          filter((agreementPreview) => agreementPreview != null),
        )
        .subscribe(({ blob, fileName }) => saveAs(blob, fileName));
    } else {
      const agreementLinks = {
        [AgreementType.PRODUCT]:
          condition_urls[`terms_and_conditions_product_${lang}`],
        [AgreementType.TRANSPORT]:
          condition_urls[`terms_and_conditions_transport_${lang}`],
        [AgreementType.BIKE]:
          condition_urls[`terms_and_conditions_bike_${lang}`],
        [AgreementType.BTM]: condition_urls[`terms_and_conditions_btm_${lang}`],
      };

      if (type in agreementLinks) {
        window.open(agreementLinks[type], "_blank");
      }
    }
  }

  private _createAction(action: IAction): IAction {
    return createAction(action, AgreementComponent);
  }

  private handleAvoEvents(
    eventType: AvoEventTypes,
    valid?: boolean,
    formTagId?: string,
    errorMessage?: string,
    checkboxName?: string,
    isChecked?: boolean,
  ) {
    switch (eventType) {
      case "button_click":
        const props = {
          uniqueName: "create_agreement",
          description: "confirmOrder",
          clickDestination:
            "https://www.ns.nl/businesscardaanvragen/confirmation",
          clickText: "Bestelling bevestigen",
          data: "form was filled in correctly: " + valid,
        };
        const trackingData = this._avoTrackingService.trackButtonClick(props);

        this._avoService.buttonClick(trackingData);
        break;
      case "form_error":
        this._avoService.formError({
          formName: "terms_and_conditions_form",
          formId1: formTagId,
          errorMessage: [errorMessage],
        });
        break;

      case "link_click":
        this._avoTrackingService.trackBackLinkClick(
          "https://www.ns.nl/businesscardaanvragen/controleren",
        );
        break;

      case "add_payment_info":
        const shippingInfo = this._avoTranslateService.addPaymentInfo(
          this.products,
        );
        this._avoService.addPaymentInfo(shippingInfo);
        break;

      case "form_submit":
        // not part of BCA avo events yet
        console.warn(`Unhandled event type: ${eventType}`);
        break;
      case "checkbox_click":
        // not part of BCA avo events yet
        this._avoService.checkboxClick({
          uniqueName: "terms_and_conditions_form_for_" + checkboxName,
          description: checkboxName,
          checked: isChecked,
          clickText: checkboxName,
          websiteSection: "ns-zakelijk|checkout|5-overeenkomst",
        });
        break;

      default:
        console.warn(`Unhandled event type: ${eventType}`);
    }
  }
}
